/**
 * NPC acts (data/npcActs.ts): every townsperson, key NPC and act person picks what to do with their
 * hands by the situation — the hour, the weather, the place, the station's work — through the World's
 * `Rng`, holds it for its span, and reacts first to what happens round them: a greeting for a
 * passer-by, a step out of the way, cowering from a red name, running from a fight (or cheering it,
 * at the arena or for the martial). The act is on the entity (`e.act`, until `e.actUntil` in World
 * time), plain data a snapshot carries, for the renderer to play.
 *
 * Townsfolk on a schedule (data/folk.ts) keep their day: the World walks them to their stations and
 * runs them from fights; this system only lays acts over the work (or keeps the work) and mirrors
 * the World's greetings and flinches as acts. The act people (`ACT_PEOPLE`) are this system's own:
 * spawned at setup, moved only here (fleeing, chasing round at tag, walking a beat, stepping aside,
 * walking home again).
 *
 * Other systems set an act on any ambient or NPC entity with `setAct` (a daily event's stall, the
 * lion dance, a gesture in a talk); until it runs out this system neither picks for that entity nor
 * moves it (the setter owns it; the World still runs townsfolk from a fight).
 */
import type { Activity } from '../data/folk';
import { dayPartAt, FOLK } from '../data/folk';
import {
  ACT_PEOPLE,
  type ActChoice,
  type ActPerson,
  FOLK_ROLES,
  FREE_WORK,
  NPC_ACTS,
  type NpcActId,
  type NpcRole,
  PLACE_OF,
  type PlaceTag,
  ROLE_ACTS,
  ROLE_REACT,
  WORK_WEIGHT,
} from '../data/npcActs';
import { periodAt } from '../data/time';
import { screenFacing, screenHeading } from '../iso';
import { pkColor } from '../karma';
import { freeAt, nearestFloor } from '../path';
import type { Entity, World } from '../world';
import type { SystemHost, WorldSystem } from './types';

declare module '../world' {
  interface Entity {
    /** The act played (data/npcActs.ts `NPC_ACTS` id), or null/absent: the base loop or the station's work. */
    act?: string | null;
    /** World time (`world.time`) the act runs to. */
    actUntil?: number;
  }
}

/** Acts set by another system (`setAct`), held over this system's picking until they run out. */
const FORCED = new WeakSet<Entity>();
/** Each act person's and townsperson's role (data/npcActs.ts), once this system has met them. */
const ROLES = new WeakMap<Entity, NpcRole>();

/** The role of an act person or townsperson (data/npcActs.ts `NpcRole`), or null for anyone without one. */
export const actRoleOf = (e: Entity): NpcRole | null => ROLES.get(e) ?? null;

/**
 * Sets an act (data/npcActs.ts id) on an entity for `seconds` of World time: the network-safe hook for other
 * systems (daily events, talks). An id the table does not know is set all the same (the renderer plays its
 * motion if it has one). `seconds` ≤ 0 clears it.
 */
export function setAct(world: World, e: Entity, id: string, seconds: number): void {
  if (seconds <= 0) return clearAct(e);
  e.act = id;
  e.actUntil = world.time + seconds;
  FORCED.add(e);
}

/** Ends an act set by `setAct` (or any act) at once. */
export function clearAct(e: Entity): void {
  e.act = null;
  e.actUntil = 0;
  FORCED.delete(e);
}

/** The act an entity is playing now (null if none or run out). */
export const actOf = (world: World, e: Entity): string | null =>
  e.act && (e.actUntil ?? 0) > world.time ? e.act : null;

/** Tiles round the player inside which people think about their acts. */
const REACH = 48;
/** A passer-by this close to an act person going their way makes them step aside (tiles). */
const ASIDE = 1.3;
/** The radius the children chase each other round at tag (tiles). */
const TAG_R = 1.7;
/** The length of a beat walked (patrol, hauling water), each way (tiles). */
const BEAT = 6;

interface Brain {
  role: NpcRole;
  place: PlaceTag | undefined;
  /** An act person (moved by this system), else a townsperson or key NPC (moved by the World). */
  own: boolean;
  homeX: number;
  homeY: number;
  /** The partner's entity id for the pair acts. */
  partner: number | null;
  prefer: ActPerson['prefer'];
  /** What the act was picked for (weather, part of day, work): picked again when it changes. */
  key: string;
  greetT: number;
  /** A walk's way: tag's angle, the beat's direction. */
  phase: number;
  dir: 1 | -1;
}

export class NpcActsSystem implements WorldSystem {
  readonly name = 'npcActs';
  private readonly brains = new Map<number, Brain>();
  /** Everyone striking or struck this step (fights the townsfolk notice). */
  private readonly fighters: Entity[] = [];

  constructor(private readonly h: SystemHost) {}

  /** The zone's act people at their spots, each picking a first act. */
  setup(): void {
    const w = this.h.world;
    const m = w.map;
    const made: (Entity | null)[] = [];
    for (const p of ACT_PEOPLE) {
      let spot: { x: number; y: number } | null = null;
      if (p.zone === w.map.zone) {
        if (p.pos) {
          const [x, y] = p.pos;
          if (x < m.w && y < m.h) spot = freeAt(m, x, y) ? { x, y } : this.floor(x, y);
        } else if (p.at) {
          const base = p.at === 'plaza' ? m.plaza : m.landmarks?.[p.at];
          if (base) spot = this.floor(base.x + (p.dx ?? 0), base.y + (p.dy ?? 0));
        }
      }
      if (!spot) {
        made.push(null);
        continue;
      }
      const e = this.h.add({
        kind: 'ambient',
        x: spot.x,
        y: spot.y,
        name: p.name,
        title: p.title,
        sprite: p.sprite,
        speed: p.child ? 2.2 : 1.6,
        radius: p.child ? 0.2 : 0.28,
      });
      made.push(e);
      ROLES.set(e, p.role);
      this.brains.set(e.id, {
        role: p.role,
        place: p.place ?? (p.at ? PLACE_OF[p.at] : undefined),
        own: true,
        homeX: spot.x,
        homeY: spot.y,
        partner: null,
        prefer: p.prefer,
        key: '',
        greetT: 0,
        phase: 0,
        dir: 1,
      });
    }
    ACT_PEOPLE.forEach((p, i) => {
      const e = made[i];
      const o = p.with !== undefined ? made[p.with] : null;
      if (e && o) this.brains.get(e.id)!.partner = o.id;
    });
  }

  private floor(x: number, y: number): { x: number; y: number } | null {
    const m = this.h.world.map;
    if (freeAt(m, x, y)) return { x, y };
    const a = nearestFloor(m, x, y, 3);
    return a ? { x: a[0], y: a[1] } : null;
  }

  /** A townsperson's or key NPC's brain, made the first time they are near. */
  private folkBrain(e: Entity): Brain | null {
    const f = e.folk!;
    const role = FOLK_ROLES[f.id];
    if (!role) return null;
    const b: Brain = {
      role,
      place: undefined,
      own: false,
      homeX: e.x,
      homeY: e.y,
      partner: null,
      prefer: undefined,
      key: '',
      greetT: 0,
      phase: 0,
      dir: 1,
    };
    this.brains.set(e.id, b);
    ROLES.set(e, role);
    return b;
  }

  step(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    this.fighters.length = 0;
    for (const o of w.entities.values())
      if (!o.dead && o.combatT > 0 && o.kind !== 'ambient' && o.kind !== 'npc') this.fighters.push(o);
    for (const e of w.entities.values()) {
      if (e.kind !== 'ambient' && e.kind !== 'npc') continue;
      let b = this.brains.get(e.id);
      if (!b && e.folk) b = this.folkBrain(e) ?? undefined;
      if (FORCED.has(e) && (e.actUntil ?? 0) <= w.time) clearAct(e);
      if (!b) continue;
      if (Math.abs(e.x - p.x) > REACH || Math.abs(e.y - p.y) > REACH) continue;
      b.greetT = Math.max(0, b.greetT - dt);
      this.actFor(e, b, dt);
    }
  }

  private set(e: Entity, id: NpcActId | null, b?: Brain): void {
    const w = this.h.world;
    if (id === null) {
      e.act = null;
      e.actUntil = 0;
      return;
    }
    const [lo, hi] = NPC_ACTS[id].dur;
    e.act = id;
    e.actUntil = w.time + lo + this.h.random() * (hi - lo);
    if (b) b.phase = this.h.random() * Math.PI * 2;
  }

  private actFor(e: Entity, b: Brain, dt: number): void {
    const w = this.h.world;
    const now = w.time;
    const react = ROLE_REACT[b.role];

    // An act another system set (`setAct`) holds until it runs out: nothing is picked, nothing moved.
    if (FORCED.has(e)) return;
    // Trouble first: a fight close by (or a red name).
    const fight = this.fightNear(e);
    if (fight) {
      const cheer = b.place === 'arena' ? 'cheer' : react.fight;
      if (e.act !== cheer || (e.actUntil ?? 0) - now < 0.5) this.set(e, cheer);
      if (b.own) {
        if (cheer === 'flee') this.run(e, fight, dt);
        else this.face(e, fight);
      }
      return;
    }

    const p = w.player;
    const d = Math.hypot(p.x - e.x, p.y - e.y);
    const color = pkColor(p.pkPoints);
    const red = !p.dead && (p.pk || color === 'red' || color === 'purple') && d < 4;

    if (!b.own) {
      // The World greets and runs: its gestures shown as the role's acts.
      const f = e.folk!;
      if (f.indoors) return this.set(e, null);
      if (f.gesture === 'cower' || f.fleeT > 0) {
        const want = red ? react.pk : e.moving ? 'flee' : 'cower';
        if (e.act !== want) this.set(e, want);
        return;
      }
      if (f.gesture === 'bow' || f.gesture === 'wave') {
        const want = f.gesture === 'wave' ? 'wave' : react.greet;
        if (e.act !== want) this.set(e, want);
        return;
      }
      if (e.moving) {
        // In stride only an act that carries on walking (an umbrella in the rain).
        if (e.act && !NPC_ACTS[e.act as NpcActId]?.walk) this.set(e, null);
        if (!e.act || (e.actUntil ?? 0) <= now) this.pick(e, b, 'walk');
        return;
      }
      if (f.walking || f.attending) {
        // A fright (cower, flee) or greeting that ran out ends here too, not only at the next pick.
        if (e.act && (e.actUntil ?? 0) <= now) this.set(e, null);
        return;
      }
      this.tick(e, b, f.act);
      return;
    }

    // An act person.
    if (red) {
      if (e.act !== react.pk) this.set(e, react.pk);
      this.face(e, p);
      return;
    }
    const cur = e.act as NpcActId | null | undefined;
    const busy = cur === 'flee' || cur === 'stepAside' || cur === react.greet || cur === 'wave';
    if (!p.dead && !busy && d < FOLK.notice) {
      // A passer-by walking into them: out of the way. Else a greeting now and then.
      const toward = p.moving && (e.x - p.x) * p.dirX + (e.y - p.y) * p.dirY > 0.4 * d;
      const move = cur ? NPC_ACTS[cur]?.move : undefined;
      if (toward && d < ASIDE && move !== 'circle') {
        this.set(e, 'stepAside', b);
        b.dir = (p.dirX * (e.y - p.y) - p.dirY * (e.x - p.x)) >= 0 ? 1 : -1;
        return;
      }
      if (b.greetT <= 0 && !p.moving) {
        b.greetT = FOLK.greetEvery;
        this.set(e, b.role === 'child' ? 'wave' : react.greet);
        this.face(e, p);
        return;
      }
    }
    this.tick(e, b, 'idle');
    this.move(e, b, dt);
  }

  /** Keeps the act, or picks the next when it has run out or the situation has changed. */
  private tick(e: Entity, b: Brain, work: Activity): void {
    const w = this.h.world;
    const key = `${w.weather.kind}|${dayPartAt(w.hour)}|${work}`;
    const left = (e.actUntil ?? 0) - w.time;
    if (left > 0 && key === b.key) return;
    // A partner's pair act is theirs to end.
    if (left > 0 && e.act && NPC_ACTS[e.act as NpcActId]?.pair && key === b.key) return;
    b.key = key;
    this.pick(e, b, work);
  }

  /** The partner when close and free for a pair act (not running, not greeting, not held by another system). */
  private partnerOf(e: Entity, b: Brain): Entity | null {
    const w = this.h.world;
    let id = b.partner;
    if (id === null && e.folk?.station?.with) id = w.folk.get(e.folk.station.with)?.id ?? null;
    const o = id !== null ? w.get(id) : undefined;
    if (!o || o.dead || FORCED.has(o) || o.moving || Math.hypot(o.x - e.x, o.y - e.y) > 3) return null;
    if (o.folk && (o.folk.indoors || o.folk.walking || o.folk.gesture)) return null;
    return o;
  }

  /** Picks the next act from the role's table for the moment (or the station's own work: null). */
  private pick(e: Entity, b: Brain, work: Activity | 'walk'): void {
    const w = this.h.world;
    const period = periodAt(w.hour).id;
    const part = dayPartAt(w.hour);
    const weather = w.weather.kind;
    const partner = this.partnerOf(e, b);
    const fits = (c: ActChoice): boolean => {
      const def = NPC_ACTS[c.act];
      if (work === 'walk') {
        if (!def.walk) return false;
      } else if (!(c.on ?? FREE_WORK).includes(work)) return false;
      if (!b.own && def.move && def.move !== 'flee') return false;
      if (def.pair && !partner) return false;
      // A move needs room: tag and a beat only for act people.
      const n = c.when;
      if (!n) return true;
      if (n.periods && !n.periods.includes(period)) return false;
      if (n.parts && !n.parts.includes(part)) return false;
      if (n.weather && !n.weather.includes(weather)) return false;
      if (n.notWeather?.includes(weather)) return false;
      if (n.places && (!b.place || !n.places.includes(b.place))) return false;
      return true;
    };
    const opts: [NpcActId | null, number][] = [];
    for (const c of ROLE_ACTS[b.role]) {
      if (!fits(c)) continue;
      const k = b.prefer?.[c.act] ?? 1;
      if (k > 0) opts.push([c.act, c.w * k]);
    }
    // The station's work (or simply standing about) is always a choice.
    if (work !== 'walk') opts.push([null, FREE_WORK.includes(work) ? 2 : WORK_WEIGHT]);
    let r = this.h.random() * opts.reduce((a, [, x]) => a + x, 0);
    let got: NpcActId | null = null;
    for (const [id, x] of opts)
      if ((r -= x) <= 0) {
        got = id;
        break;
      }
    if (got === null) {
      // Back to the work (or the base) for a while before the next pick.
      e.act = null;
      e.actUntil = w.time + 6 + this.h.random() * 10;
      return;
    }
    this.set(e, got, b);
    if (NPC_ACTS[got].pair && partner) {
      // Both at it together, facing.
      partner.act = got;
      partner.actUntil = e.actUntil;
      const pb = this.brains.get(partner.id);
      if (pb) pb.key = b.key;
      this.face(e, partner);
      this.face(partner, e);
    }
    if (got === 'tag' && partner) {
      const pb = this.brains.get(partner.id);
      if (pb && pb.own) {
        partner.act = 'tag';
        partner.actUntil = e.actUntil;
        pb.phase = b.phase + Math.PI * 0.8;
        pb.key = b.key;
      }
    }
  }

  /** An act person's body: tag round their spot, the beat, a step aside; else back home. */
  private move(e: Entity, b: Brain, dt: number): void {
    const act = e.act as NpcActId | null | undefined;
    const mv = act ? NPC_ACTS[act]?.move : undefined;
    e.moving = false;
    e.sprinting = false;
    if (mv === 'circle') {
      b.phase += (e.speed / TAG_R) * dt;
      const tx = b.homeX + Math.cos(b.phase) * TAG_R;
      const ty = b.homeY + Math.sin(b.phase) * TAG_R;
      this.walkTo(e, tx, ty, e.speed, dt);
      return;
    }
    if (mv === 'patrol') {
      const tx = b.homeX + b.dir * BEAT;
      if (this.walkTo(e, tx, b.homeY, e.speed * 0.6, dt) < 0.2) b.dir = b.dir === 1 ? -1 : 1;
      return;
    }
    if (mv === 'aside') {
      // Half a pace out of the way, across the passer-by's line.
      const p = this.h.world.player;
      const l = Math.hypot(p.dirX, p.dirY) || 1;
      const sx = (-p.dirY / l) * b.dir;
      const sy = (p.dirX / l) * b.dir;
      const k = 1.6 * dt;
      this.stepBy(e, sx * k, sy * k);
      this.face(e, p);
      return;
    }
    // Back to their spot when nothing moves them.
    if (Math.hypot(b.homeX - e.x, b.homeY - e.y) > 0.3) {
      this.walkTo(e, b.homeX, b.homeY, e.speed * 0.7, dt);
      if (act && !NPC_ACTS[act]?.walk && act !== 'flee') this.set(e, null);
    } else if (act && NPC_ACTS[act]?.pair && b.partner !== null) {
      const o = this.h.world.get(b.partner);
      if (o) this.face(e, o);
    }
  }

  /** Walks straight toward a point; returns the distance left. */
  private walkTo(e: Entity, tx: number, ty: number, speed: number, dt: number): number {
    const dx = tx - e.x;
    const dy = ty - e.y;
    const l = Math.hypot(dx, dy);
    if (l < 0.05) return l;
    const s = Math.min(l, speed * dt);
    if (this.stepBy(e, (dx / l) * s, (dy / l) * s)) e.moving = true;
    return l - s;
  }

  /** Runs away from a point (an act person). */
  private run(e: Entity, from: { x: number; y: number }, dt: number): void {
    const dx = e.x - from.x;
    const dy = e.y - from.y;
    const l = Math.hypot(dx, dy) || 1;
    e.moving = false;
    if (l > FOLK.danger) return this.face(e, from);
    const s = e.speed * 1.8 * dt;
    if (this.stepBy(e, (dx / l) * s, (dy / l) * s)) {
      e.moving = true;
      e.sprinting = true;
    } else this.face(e, from);
  }

  /** A step on open ground, turning to it. */
  private stepBy(e: Entity, dx: number, dy: number): boolean {
    const m = this.h.world.map;
    const x = e.x + dx;
    const y = e.y + dy;
    this.dir(e, dx, dy);
    if (!freeAt(m, x, y, e.radius)) return false;
    e.x = x;
    e.y = y;
    return true;
  }

  private face(e: Entity, t: { x: number; y: number }): void {
    this.dir(e, t.x - e.x, t.y - e.y);
  }

  private dir(e: Entity, dx: number, dy: number): void {
    const l = Math.hypot(dx, dy);
    if (l < 1e-4) return;
    const sf = screenFacing(dx, dy);
    if (Math.abs(sf) > 1e-4) e.facing = sf > 0 ? 1 : -1;
    e.heading = screenHeading(dx, dy);
    e.dirX = dx / l;
    e.dirY = dy / l;
  }

  /** The nearest blow being struck within `FOLK.danger` (anyone fighting but the townsfolk's friendly bouts). */
  private fightNear(e: Entity): Entity | null {
    let best: Entity | null = null;
    let bd: number = FOLK.danger;
    for (const o of this.fighters) {
      if (Math.abs(o.x - e.x) > bd || Math.abs(o.y - e.y) > bd) continue;
      const d = Math.hypot(o.x - e.x, o.y - e.y);
      if (d < bd) [best, bd] = [o, d];
    }
    return best;
  }
}
